'use client';

import { UserIcon } from '@wolfejs/icons/IconMap';
import nameInitials from 'name-initials';
import React from 'react';
import { Box } from '../Box';
import { useTheme } from '../hooks/useTheme';
import { UIStyling } from '../styles/UIStyling';
import type { Placement, UIColor, UIThemableComponent } from '../types/UI';
import { UIColors, UIShapes, UIShapesExtra, UISizes, UIVariants } from '../types/UI';
import { cn } from '../utils/classnames';

export type AvatarProps = UIThemableComponent & {
  ref?: React.ForwardedRef<HTMLDivElement>;

  src?: string;
  alt?: string;
  bordered?: boolean;
  dotIndicator?: UIColor;
  dotIndicatorPlacement?: Placement;
  shouldFallbackToInitials?: boolean;
  fullname?: string;
};

const AvatarComp = (
  {
    cname = 'ui-avatar',
    children,
    className,

    // ----- Themable props defaults
    color = UIColors.default,
    size = UISizes.md,
    variant = UIVariants.solid,
    shape = UIShapesExtra.circle,
    gradient,
    elevation,

    // ----- Options
    src,
    alt,
    bordered,
    dotIndicator,
    dotIndicatorPlacement,
    shouldFallbackToInitials = false,
    fullname = '',

    ...otherProps
  }: AvatarProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const { theme } = useTheme();
  const themableProps = { variant, color, shape, size, theme, gradient, elevation };

  return (
    <Box
      noPad
      ref={forwardedRef}
      cname={cname}
      {...themableProps}
      {...otherProps}
      className={cn(
        'justify-centers relative inline-flex items-center',
        // flowbite using a specific color for the alert background and foreground
        { [`bg-${color}-100 text-${color}-600 dark:bg-${color}-600 dark:text-${color}-300`]: theme === 'flowbite' },
        {
          [`text-${color}-foreground bg-gradient-to-r from-${color} via-${color}-hover to-${color}-active `]:
            variant === UIVariants.solid && gradient,
        },
        { [`ring-border ring-2`]: bordered },
        className
      )}
    >
      <div
        className={cn(
          'justify-centers  relative flex flex-1 items-center overflow-hidden',
          UIStyling.shapes(themableProps),
          UIStyling.squareSizes(themableProps),
          UIStyling.elevations(themableProps)
        )}
      >
        {src && (
          <picture>
            <img src={src} alt={fullname ? nameInitials(fullname) : alt || 'avatar'} />
          </picture>
        )}
        <div className="flex-1 text-center">{children}</div>
        {!children && !src && (
          <div
            className={cn(
              '-left-[4px] -top-[1px] flex h-12 w-12 items-center justify-center overflow-hidden',
              UIStyling.shapes(themableProps)
            )}
          >
            {shouldFallbackToInitials ? <span>{nameInitials(fullname)}</span> : <UserIcon className="h-8 w-8" />}
          </div>
        )}
      </div>
      {dotIndicator && (
        <span
          className={cn(
            ` absolute h-3.5 w-3.5 transform rounded-full border-2 border-white`,
            `bg-${dotIndicator}`,
            { 'bg-success': dotIndicator === UIColors.success },
            {
              '-top-2':
                dotIndicatorPlacement === 'T' || dotIndicatorPlacement === 'TR' || dotIndicatorPlacement === 'TL',
              '-right-1':
                dotIndicatorPlacement === 'R' || dotIndicatorPlacement === 'TR' || dotIndicatorPlacement === 'BR',
              '-left-1':
                dotIndicatorPlacement === 'L' || dotIndicatorPlacement === 'TL' || dotIndicatorPlacement === 'BL',
              '-bottom-2':
                dotIndicatorPlacement === 'BR' || dotIndicatorPlacement === 'BL' || dotIndicatorPlacement === 'B',
            }
          )}
        ></span>
      )}
    </Box>
  );
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  AvatarComp
) as React.ForwardRefExoticComponentExtended<AvatarProps>;

export const AvatarShapes = [...Object.values(UIShapes), UIShapesExtra.circle];
